import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, ChevronDown, Check } from 'lucide-react';

const PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal',
  'Limpopo', 'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape',
];

/**
 * Compact pill-style province dropdown.
 *
 * The dropdown menu itself is portalled to <body> so it cannot be clipped by
 * any scrolling/overflow ancestor (which was the case when this lived inside
 * the horizontal pill scroll-strip).
 */
export const ProvincePillDropdown = ({ value = 'all', onChange, testId = 'province-pill' }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Position menu under the trigger (portal lives in document.body)
  useLayoutEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, left: rect.left });
    }
  }, [open]);

  // Close on outside click + Escape + scroll/resize (so menu never detaches from trigger)
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const handleScrollOrResize = () => setOpen(false);
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScrollOrResize, { passive: true, capture: true });
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScrollOrResize, { capture: true });
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open]);

  const active = value && value !== 'all';
  const label = active ? value : 'All Provinces';

  const pick = (val) => {
    onChange?.(val);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold transition-all whitespace-nowrap flex-shrink-0 ${
          active
            ? 'bg-[#FFD12D] text-[#252525]'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
        }`}
      >
        <MapPin className="w-3 h-3" />
        <span className="truncate max-w-[110px]">{label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 2000 }}
          className="min-w-[180px] bg-white dark:bg-[#252525] border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl py-1 max-h-72 overflow-auto"
          data-testid={`${testId}-menu`}
        >
          <button
            onClick={() => pick('all')}
            className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-left hover:bg-[#FFD12D]/10 ${
              !active ? 'text-[#FFD12D] font-semibold' : 'text-gray-700 dark:text-gray-300'
            }`}
            data-testid={`${testId}-opt-all`}
          >
            All Provinces
            {!active && <Check className="w-3.5 h-3.5" />}
          </button>
          <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
          {PROVINCES.map((p) => (
            <button
              key={p}
              onClick={() => pick(p)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-left hover:bg-[#FFD12D]/10 ${
                value === p ? 'text-[#FFD12D] font-semibold' : 'text-gray-700 dark:text-gray-300'
              }`}
              data-testid={`${testId}-opt-${p}`}
            >
              {p}
              {value === p && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default ProvincePillDropdown;
